import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router';
import '../../styles/common.css';
import AuthShowcase from './AuthShowcase';
import { AnimatedSocialButton } from '../animations/AnimatedFormElements';
import MFAVerificationModal from './MFAVerificationModal';
import ResumeWizardModal from '../wizard/modals/ResumeWizardModal';
import axiosInstance, { resetSessionExpiredFlag } from '../../api/axiosInstance';
import { useOnboarding } from '../../context/OnboardingContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useProject } from '../../context/ProjectContext';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { intentToApiValue, STEP_STATUS } from '../../Config/Onboarding/constant';
import { fetchLastWorkspaceDashboardTarget } from '../../utils/authRedirectUtils';
import {
  getPendingVerificationEmail,
  isUnverifiedLoginError,
  clearPendingVerificationEmail,
} from '../../utils/verificationEmailUtils';
import ResendVerificationEmail from './ResendVerificationEmail';
import '../../styles/auth-login.css';
import { setDateTimePrefs } from '../../utils/dateTimeFormat';
import { setTourCompleted } from '../../utils/productTourStatus';

const Login = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setStepStatus, markStepCompleted, initializeStepFlow , nextStep:moveNextStep} = useOnboarding();
  const { selectWorkspace } = useWorkspace();
  const { selectProject } = useProject();
    
  
  // Extract wizard context from query params
  const returnTo = searchParams.get('return_to');
  const draftSessionId = searchParams.get('draft_session_id');
  const nextStep = searchParams.get('next_step');
  
  // IMPORTANT: If embedded mode, always treat as wizard flow
  const isWizardFlow = embedded || returnTo === 'wizard';
  const [loginMethod, setLoginMethod] = useState('email');
  const [formData, setFormData] = useState({
    email: '',
    mobileNumber: '',
    password: '',
    rememberMe: false
  });

  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [errors, setErrors] = useState({});
  const [showMFAModal, setShowMFAModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [loginResponse, setLoginResponse] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showUnverifiedPrompt, setShowUnverifiedPrompt] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');

  const getStoredObject = (key) => {
    try {
      return JSON.parse(localStorage.getItem(key) || '{}');
    } catch (error) {
      return {};
    }
  };

  const redirectToDashboard = async () => {
    try {
      const { path, lastWorkspace, lastProject } = await fetchLastWorkspaceDashboardTarget();

      if (lastWorkspace?.id && lastProject?.project_id) {
        selectWorkspace(lastWorkspace);
        selectProject(String(lastProject.project_id), lastProject.project_name || '');
        navigate(path);
        return;
      }
    } catch (error) {
      console.error('Failed to resolve post-login dashboard path:', error);
    }

    navigate('/dashboard');
  };

  useEffect(() => {
    sessionStorage.removeItem('isLoggingOut');

    const pendingEmail = getPendingVerificationEmail();
    if (pendingEmail) {
      setFormData((prev) => ({ ...prev, email: pendingEmail }));
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = name === 'mobileNumber'
      ? value.replace(/\D/g, '').slice(0, 10)
      : value;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : nextValue
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setErrors({});
    setLoginError('');
    setShowUnverifiedPrompt(false);
  };

  const getLoginIdentifier = () => (
    loginMethod === 'email' ? formData.email : formData.mobileNumber
  );

  const buildLoginPayload = () => {
    const loginPayload = {
      password: formData.password
    };

    if (loginMethod === 'email') {
      loginPayload.username = formData.email;
    } else {
      loginPayload.mobile_number = formData.mobileNumber;
    }

    const wizardIntent = localStorage.getItem('wizard_intent');
    if (isWizardFlow && wizardIntent) {
      const intentNumValue = intentToApiValue(wizardIntent);
      if (intentNumValue) {
        loginPayload.intent = intentNumValue;
      }
    }

    return loginPayload;
  };

  const validate = () => {
    const newErrors = {};

    if (loginMethod === 'email') {
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email address is invalid';
      }
    } else if (!formData.mobileNumber) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Mobile number must be 10 digits';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleResumeWizard = () => {
    // This is called by ResumeWizardModal after successful restoration
    // The navigation is handled by the modal itself
    console.log('[Login] Resume wizard successful, modal will handle navigation');
  };

  const handleSkipResume = () => {
    // Skip resume and go to step 2
    markStepCompleted(1);
    moveNextStep();
    setStepStatus(2, STEP_STATUS.IN_PROGRESS);
    setShowResumeModal(false);
    console.log('[Login] User skipped resume, navigating to step 2');
    navigate('/app/onboarding?step=2');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsLoading(true);
    setLoginError('');
    setShowUnverifiedPrompt(false);

    try {
      const loginIdentifier = getLoginIdentifier();
      const loginPayload = buildLoginPayload();
      const wizardIntent = localStorage.getItem('wizard_intent');

      const response = await axiosInstance.post(`/users/logIn`, loginPayload);
       
      if (response.detail.mfa_required === true) {
        localStorage.setItem('tempLoginData', JSON.stringify({
          username: loginIdentifier,
          loginMethod,
          password: formData.password,
          userId: response.detail.user_id,
          user_type: response.detail.user_type,
          is_wizard_completed: response.detail.is_wizard_completed,
          account_settings: response.detail.account_settings || null,
          tour_completed: response.detail.tour_completed
        }));
        setShowMFAModal(true);
      } else if (response.code === 200 && !response.detail.mfa_required) {
        const userData = {
          username: loginIdentifier,
          userId: response.detail.user_id,
          accessToken: response.detail.access_token,
          user_type: response.detail.user_type,
          is_wizard_completed: response.detail.is_wizard_completed
        };
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('isAuthenticated', 'true');
        setDateTimePrefs(response.detail.account_settings);
        setTourCompleted(response.detail.tour_completed);
        clearPendingVerificationEmail();
        resetSessionExpiredFlag();
        initializeStepFlow(wizardIntent);

        
        // Store the full response for later use
        setLoginResponse(response.detail);
        
        // CONTEXT-AWARE REDIRECT LOGIC
        console.log('[Login] embedded:', embedded, 'returnTo:', returnTo, 'isWizardFlow:', isWizardFlow);
        console.log('[Login] is_wizard_completed:', response.detail.is_wizard_completed);
        const wizardStatus = response.detail.is_wizard_completed;

        if (isWizardFlow) {
          // Check wizard completion status
          
          if (wizardStatus === null) {
            // New user or first time - navigate to step 2
            markStepCompleted(1);
            moveNextStep();
            setStepStatus(2, STEP_STATUS.IN_PROGRESS);
            console.log('[Login] Wizard flow detected (null status), navigating to step 2');
            navigate('/app/onboarding?step=2');
          } else if (wizardStatus === false) {
            // Incomplete wizard - show resume modal
            // console.log('[Login] Incomplete wizard detected, showing resume modal');
            setShowResumeModal(true);
          } else if (wizardStatus === true) {
            // Completed wizard - redirect to dashboard
            //             markStepCompleted(1);
            // moveNextStep();
            // setStepStatus(2, STEP_STATUS.IN_PROGRESS);
            // console.log('[Login] Wizard flow detected (null status), navigating to step 2');
            // navigate('/app/onboarding?step=2');
            // console.log('[Login] Wizard completed, redirecting to dashboard');
            await redirectToDashboard();
          }
        } else {
            if (wizardStatus === null) {
            // New user or first time - navigate to step 2
            markStepCompleted(1);
            moveNextStep();
            setStepStatus(2, STEP_STATUS.IN_PROGRESS);
            console.log('[Login] Wizard flow detected (null status), navigating to step 2');
            navigate('/app/onboarding?step=2');
          } else if (wizardStatus === false) {
            // Incomplete wizard - show resume modal
            // console.log('[Login] Incomplete wizard detected, showing resume modal');
            setShowResumeModal(true);
          } else if (wizardStatus === true) {
            // Completed wizard - redirect to dashboard
            //             markStepCompleted(1);
            // moveNextStep();
            // setStepStatus(2, STEP_STATUS.IN_PROGRESS);
            // console.log('[Login] Wizard flow detected (null status), navigating to step 2');
            // navigate('/app/onboarding?step=2');
            // console.log('[Login] Wizard completed, redirecting to dashboard');
            await redirectToDashboard();
          }
          // NORMAL LOGIN FLOW: Redirect to dashboard
          // console.log('[Login] Normal login flow, redirecting to dashboard');
          // navigate('/dashboard');
        }
      } else {
        setLoginError('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (isUnverifiedLoginError(error)) {
        const emailForResend =
          loginMethod === 'email'
            ? formData.email
            : getPendingVerificationEmail();
        setUnverifiedEmail(emailForResend);
        setShowUnverifiedPrompt(true);
        setLoginError('');
      } else {
        setLoginError(
          error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          (loginMethod === 'email'
            ? 'Invalid email or password. Please try again.'
            : 'Invalid phone number or password. Please try again.')
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const renderLoginMethodTabs = () => (
    <div className="auth-login-tabs">
      <button
        type="button"
        className={`auth-login-tab ${loginMethod === 'email' ? 'active' : ''}`}
        onClick={() => switchLoginMethod('email')}
      >
        Email
      </button>
      <button
        type="button"
        className={`auth-login-tab ${loginMethod === 'phone' ? 'active' : ''}`}
        onClick={() => switchLoginMethod('phone')}
      >
        Phone
      </button>
    </div>
  );

  const renderCredentialField = () => (
    loginMethod === 'email' ? (
      <div className="form-group">
        <label htmlFor="email" className="form-label">
          Email
        </label>
        <input
          type="email"
          id="email"
          name="email"
          className="form-input"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="email"
        />
        {errors.email && (
          <div className="form-error">{errors.email}</div>
        )}
      </div>
    ) : (
      <div className="form-group">
        <label htmlFor="mobileNumber" className="form-label">
          Phone Number
        </label>
        <input
          type="tel"
          id="mobileNumber"
          name="mobileNumber"
          className="form-input"
          placeholder="Enter your phone number"
          maxLength={10}
          value={formData.mobileNumber}
          onChange={handleChange}
          autoComplete="tel"
        />
        {errors.mobileNumber && (
          <div className="form-error">{errors.mobileNumber}</div>
        )}
      </div>
    )
  );

  const handleBackToLogin = () => {
    setShowUnverifiedPrompt(false);
    setLoginError('');
  };

  const renderUnverifiedView = () => (
    <div>
      <div className="resend-verification-info">
        <p className="resend-verification-info-title">
          Your account isn&apos;t verified yet. Enter your email to receive a new verification link.
        </p>
        <ResendVerificationEmail
          defaultEmail={unverifiedEmail || formData.email}
          showEmailInput
          variant="compact"
        />
      </div>
      <button
        type="button"
        className="btn-link"
        onClick={handleBackToLogin}
        style={{ display: 'block', width: '100%', marginTop: '16px', textAlign: 'center' }}
      >
        Back to Sign In
      </button>
    </div>
  );

  const renderLoginForm = (showForgotPassword = false) => (
    <form onSubmit={handleSubmit}>
      {loginError && (
        <div className="form-error mb-4" style={{ textAlign: 'center' }}>
          {loginError}
        </div>
      )}

      {renderLoginMethodTabs()}
      {renderCredentialField()}
      {renderPasswordField(showForgotPassword)}

      <div className="remember-me-option">
        <input
          type="checkbox"
          id="rememberMe"
          name="rememberMe"
          checked={formData.rememberMe}
          onChange={handleChange}
          className="remember-me-checkbox"
        />
        <label htmlFor="rememberMe" className="remember-me-label">
          Remember me
        </label>
      </div>

      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={isLoading}
      >
        {isLoading ? 'Signing In...' : 'Sign In'}
      </button>

      {!embedded && (
        <div className="auth-get-started">
          <span className="auth-get-started__divider"><span>New to RobiTest?</span></span>
          <button
            type="button"
            className="btn btn-block auth-get-started__btn"
            onClick={() => navigate('/get-started')}
            disabled={isLoading}
          >
            Get started with guided setup →
          </button>
        </div>
      )}
    </form>
  );

  const renderPasswordField = (showForgotPassword = false) => (
    <div className="form-group">
      <div className="d-flex justify-between align-center mb-1">
        <label htmlFor="password" className="form-label">
          Password
        </label>
        {showForgotPassword && (
          <button
            type="button"
            className="btn-link"
            onClick={() => navigate("/forgot-password")}
          >
            Forgot Password?
          </button>
        )}
      </div>
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          id="password"
          name="password"
          className="form-input w-full pr-10 text-sm"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleChange}
          autoComplete="current-password"
        />

        <span
          onClick={() => setShowPassword((prev) => !prev)}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 hover:text-gray-700"
        >
          {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </span>
      </div>

      {errors.password && (
        <div className="form-error">{errors.password}</div>
      )}
    </div>
  );

  // If embedded mode, return just the form
  if (embedded) {
    return (
      <>
        {showUnverifiedPrompt ? renderUnverifiedView() : renderLoginForm(false)}

        {showMFAModal && (
          <MFAVerificationModal
            isOpen={showMFAModal}
            onClose={() => setShowMFAModal(false)}
            username={getLoginIdentifier()}
            password={formData.password}
          />
        )}

        {showResumeModal && (
          <ResumeWizardModal
            isOpen={showResumeModal}
            onClose={() => setShowResumeModal(false)}
            onResume={handleResumeWizard}
            onSkip={handleSkipResume}
          />
        )}
      </>
    );
  }

  // Full standalone login page
  return (
    <div className="auth-container auth-container--rt">
      {!isMobile && (
        <div className="auth-left-section">
          <AuthShowcase />
        </div>
      )}

      <div className={`auth-right-section ${isMobile ? "mobile-full" : ""}`}>
        <div className="auth-card">
          <div className="auth-header">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="60"
              height="60"
              viewBox="0 0 215 215"
              fill="none"
              className="auth-logo"
            >
              <path
                d="M115 10C115 4.47715 110.523 0 105 0C99.4772 0 95 4.47715 95 10V30C95 35.5228 99.4772 40 105 40C110.523 40 115 35.5228 115 30V10Z"
                fill="#4285F4"
              />
              <path
                d="M105 60C66.3401 60 35 91.3401 35 130V170C35 192.091 52.9086 210 75 210H135C157.091 210 175 192.091 175 170V130C175 91.3401 143.66 60 105 60Z"
                stroke="#4285F4"
                strokeWidth="20"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M75 130C75 130 75 120 75 115C75 110 80 105 85 105C90 105 95 110 95 115C95 120 95 130 95 130"
                stroke="#4285F4"
                strokeWidth="20"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M115 130C115 130 115 120 115 115C115 110 120 105 125 105C130 105 135 110 135 115C135 120 135 130 135 130"
                stroke="#4285F4"
                strokeWidth="20"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M75 170H135"
                stroke="#4285F4"
                strokeWidth="20"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h1 className="auth-title">
              {showUnverifiedPrompt ? 'Verify Your Email' : 'Welcome Back'}
            </h1>
            <p className="auth-subtitle">
              {showUnverifiedPrompt
                ? 'We need to verify your email before you can sign in'
                : 'Sign in with your email or phone number to continue'}
            </p>
          </div>

          <div className="auth-body">
            {showUnverifiedPrompt ? renderUnverifiedView() : renderLoginForm(true)}
          </div>

          <div className="auth-footer">
            <p>&copy; {new Date().getFullYear()} RobiTest. All rights reserved.</p>
          </div>
        </div>
        {isMobile && (
          <p className="auth-page-copy">&copy; {new Date().getFullYear()} RobiTest. All rights reserved.</p>
        )}
      </div>
      {showMFAModal && (
        <MFAVerificationModal
          onClose={() => setShowMFAModal(false)}
          onVerified={() => {
            setShowMFAModal(false);
            // Apply same context-aware redirect logic after MFA
            if (isWizardFlow) {
              const resumeStep = nextStep ? parseInt(nextStep, 10) : 2;
              navigate(`/app/onboarding?step=${resumeStep}`);
            } else {
              redirectToDashboard();
            }
          }}
        />
      )}

      {showResumeModal && (
        <ResumeWizardModal
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          onResume={handleResumeWizard}
          onSkip={handleSkipResume}
        />
      )}
    </div>
  );
};

export default Login;